import { createSlice } from '@reduxjs/toolkit';
import { cancelSubscriptionThunk, changePlanThunk, checkoutPlanThunk, fetchCurrentPlanDetailsThunk, fetchProratedChargeThunk, getPlansThunk, specialUpgradePlanThunk } from '../thunks/subscription';
import { PlanInvoiceObject, PlanObject, SubscriptionObject, SubscriptionState } from '../../params/redux/subscription';

// Initial state with default values for authentication and user info
const initialState: SubscriptionState = {
  // Auth token stored after successful login
  plans: [],
  selectedPlan: null,
  subscription: null,
  subscribed: false,
  invoices: [],
  selectedInvoice: null,
  prorateCharge: 0,
  loaders: {
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isLoading: false,
    isSearching: false,
    isInserting: false
  }
};

// Slice for app-related states and actions
const subscriptionSlice = createSlice({
  // Slice name
  name: 'subscription',
  // inital state for the slice
  initialState,
  reducers: {
    settingSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    settingSelectedInvoice: (state, action) => {
      state.selectedInvoice = action.payload;
    },
    // Resets loading states related to app actions
    resettingSubscriptionLoader: (state) => {
      state.loaders = initialState.loaders;
    },
    // Resets all app data to the initial state
    resettingSubscriptionData: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPlansThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(getPlansThunk.fulfilled, (state, action) => {
        state.plans = action.payload as PlanObject[];
      })
      .addCase(getPlansThunk.rejected, (state) => {
        state.plans = [];
      });
    builder
      .addCase(checkoutPlanThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(checkoutPlanThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(checkoutPlanThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(changePlanThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(changePlanThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(changePlanThunk.rejected, () => {
        // Reset user data on logout failure as well
      });
    builder
      .addCase(cancelSubscriptionThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isDeleting = true;
      })
      .addCase(cancelSubscriptionThunk.fulfilled, (state, action) => {
        // Reset user data and login status on successful logout
        if (action.payload) {
          state.subscription = null;
          state.selectedPlan = null;
          state.invoices = [];
          state.plans = [];
          state.prorateCharge = 0;
          state.subscribed = false;
        }
      })
      .addCase(cancelSubscriptionThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.subscription = null;
        state.selectedPlan = null;
        state.invoices = [];
        state.plans = [];
        state.prorateCharge = 0;
        state.subscribed = false;
      });
    builder
      .addCase(fetchProratedChargeThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(fetchProratedChargeThunk.fulfilled, (state, action) => {
        // Reset user data and login status on successful logout
        state.prorateCharge = action.payload as number;
      })
      .addCase(fetchProratedChargeThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.prorateCharge = 0;
      });
    builder
      .addCase(fetchCurrentPlanDetailsThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(fetchCurrentPlanDetailsThunk.fulfilled, (state, action) => {
        // Reset user data and login status on successful logout
        const { subscription, invoices } = action.payload as {
          subscription: SubscriptionObject;
          invoices: PlanInvoiceObject[];
        };
        state.subscription = subscription;
        state.invoices = invoices;
      })
      .addCase(fetchCurrentPlanDetailsThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.subscription = null;
        state.invoices = [];
      });
    builder
      .addCase(specialUpgradePlanThunk.pending, (state) => {
        // Set loading flag during logout request
        state.loaders.isLoading = true;
      })
      .addCase(specialUpgradePlanThunk.fulfilled, () => {
        // Reset user data and login status on successful logout
      })
      .addCase(specialUpgradePlanThunk.rejected, (state) => {
        // Reset user data on logout failure as well
        state.subscription = null;
        state.invoices = [];
      });
  }
});

// Exporting actions for updating app state
export const {
  settingSelectedInvoice,
  settingSelectedPlan,
  resettingSubscriptionData,
  resettingSubscriptionLoader
} = subscriptionSlice.actions;

// Exporting the reducer to be used in the store
export default subscriptionSlice.reducer;
